import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { isValid as isValidLicense } from 'driver-license-validator';
import {
  MVR_FLOW_ACTIVITY_HISTORY,
  MVR_FLOW_TRANSACTIONAL,
  MvrFlow,
  MvrOrderFee,
  MvrOrderResult,
  getMvrOrderFee,
  getMvrOrderStatus,
  placeMvrOrder,
} from '../../services/mvrOrderApi';

// The Toolbox MVR ordering flow in MTM styling: pick driver → record type →
// license details → confirm the charge → place → poll until ready. Validation
// wording matches the Toolbox so both products read the same.

interface DriverLike {
  name?: string;
  firstName?: string;
  middleName?: string;
  lastName?: string;
  licenseNumber?: string;
  state?: string;          // quote drivers carry the licence state as `state`
  licenseState?: string;
  dateOfBirth?: string;
  gender?: string;
}

interface OrderMvrModalProps {
  quoteId: string;
  drivers: DriverLike[];
  insuredName?: string;
  onClose: () => void;
  onOrderComplete: (orderId: string, reportId: string, flow: MvrFlow) => void;
}

// Licensing states, matching the Toolbox's list.
const US_STATES = [
  { code: 'AK', name: 'Alaska' },
  { code: 'AL', name: 'Alabama' },
  { code: 'AR', name: 'Arkansas' },
  { code: 'AZ', name: 'Arizona' },
  { code: 'CA', name: 'California' },
  { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' },
  { code: 'DC', name: 'District of Columbia' },
  { code: 'DE', name: 'Delaware' },
  { code: 'FL', name: 'Florida' },
  { code: 'GA', name: 'Georgia' },
  { code: 'HI', name: 'Hawaii' },
  { code: 'IA', name: 'Iowa' },
  { code: 'ID', name: 'Idaho' },
  { code: 'IL', name: 'Illinois' },
  { code: 'IN', name: 'Indiana' },
  { code: 'KS', name: 'Kansas' },
  { code: 'KY', name: 'Kentucky' },
  { code: 'LA', name: 'Louisiana' },
  { code: 'MA', name: 'Massachusetts' },
  { code: 'MD', name: 'Maryland' },
  { code: 'ME', name: 'Maine' },
  { code: 'MI', name: 'Michigan' },
  { code: 'MN', name: 'Minnesota' },
  { code: 'MO', name: 'Missouri' },
  { code: 'MS', name: 'Mississippi' },
  { code: 'MT', name: 'Montana' },
  { code: 'NC', name: 'North Carolina' },
  { code: 'ND', name: 'North Dakota' },
  { code: 'NE', name: 'Nebraska' },
  { code: 'NH', name: 'New Hampshire' },
  { code: 'NJ', name: 'New Jersey' },
  { code: 'NM', name: 'New Mexico' },
  { code: 'NV', name: 'Nevada' },
  { code: 'NY', name: 'New York' },
  { code: 'OH', name: 'Ohio' },
  { code: 'OK', name: 'Oklahoma' },
  { code: 'OR', name: 'Oregon' },
  { code: 'PA', name: 'Pennsylvania' },
  { code: 'RI', name: 'Rhode Island' },
  { code: 'SC', name: 'South Carolina' },
  { code: 'SD', name: 'South Dakota' },
  { code: 'TN', name: 'Tennessee' },
  { code: 'TX', name: 'Texas' },
  { code: 'UT', name: 'Utah' },
  { code: 'VA', name: 'Virginia' },
  { code: 'VT', name: 'Vermont' },
  { code: 'WA', name: 'Washington' },
  { code: 'WI', name: 'Wisconsin' },
  { code: 'WV', name: 'West Virginia' },
  { code: 'WY', name: 'Wyoming' },
];

// Matches the Toolbox's rate-limit wait and wording.
const RATE_LIMIT_MESSAGE = 'Please wait 20 seconds before submitting another MVR request.';
const MIN_DRIVER_AGE_YEARS = 16;
const POLL_INTERVAL_MS = 5000;
const POLL_MAX_ATTEMPTS = 24; // ~2 minutes

type Errors = Partial<Record<
  'firstName' | 'lastName' | 'licenseState' | 'licenseNumber' | 'birthDate' | 'recordType', string
>>;

/** Driver records carry either a code or a full name. */
function stateCode(value: string): string {
  const v = (value || '').trim();
  if (!v) return '';
  const match = US_STATES.find(
    (st) => st.code === v.toUpperCase() || st.name.toLowerCase() === v.toLowerCase(),
  );
  return match ? match.code : '';
}

function driverLabel(d: DriverLike): string {
  return d.name || [d.firstName, d.lastName].filter(Boolean).join(' ') || d.licenseNumber || 'Driver';
}

function splitName(d: DriverLike): { firstName: string; middleName: string; lastName: string } {
  if (d.firstName || d.lastName) {
    return { firstName: d.firstName || '', middleName: d.middleName || '', lastName: d.lastName || '' };
  }
  const parts = (d.name || '').trim().split(/\s+/).filter(Boolean);
  return {
    firstName: parts[0] || '',
    middleName: parts.length > 2 ? parts.slice(1, -1).join(' ') : '',
    lastName: parts.length > 1 ? parts[parts.length - 1] : '',
  };
}

/** Coerce a date to YYYY-MM-DD. Quote drivers carry display formats such as
 *  "Jun 23, 1981 12:00 am", which a date input renders blank and DataFabric
 *  rejects. Returns "" when the value cannot be parsed. */
function toIsoDate(value?: string): string {
  const raw = (value || '').trim();
  if (!raw) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
  if (/^\d{4}-\d{2}-\d{2}[T ]/.test(raw)) return raw.slice(0, 10);
  const parsed = new Date(raw.replace(/(\d)(am|pm)$/i, '$1 $2'));
  if (Number.isNaN(parsed.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${parsed.getFullYear()}-${pad(parsed.getMonth() + 1)}-${pad(parsed.getDate())}`;
}

/** Years between a YYYY-MM-DD string and today; NaN when unparseable. */
function ageInYears(iso: string): number {
  const dob = new Date(iso);
  if (Number.isNaN(dob.getTime())) return NaN;
  const now = new Date();
  const age = now.getFullYear() - dob.getFullYear();
  const before = now.getMonth() < dob.getMonth()
    || (now.getMonth() === dob.getMonth() && now.getDate() < dob.getDate());
  return before ? age - 1 : age;
}

const OrderMvrModal: React.FC<OrderMvrModalProps> = ({
  quoteId, drivers, insuredName, onClose, onOrderComplete,
}) => {
  const [selectedIdx, setSelectedIdx] = useState(drivers.length ? 0 : -1);
  const [flow, setFlow] = useState<MvrFlow>(MVR_FLOW_TRANSACTIONAL);
  const [recordType, setRecordType] = useState('');
  const [firstName, setFirstName] = useState('');
  const [middleName, setMiddleName] = useState('');
  const [lastName, setLastName] = useState('');
  const [licenseState, setLicenseState] = useState('');
  const [licenseNumber, setLicenseNumber] = useState('');
  const [birthDate, setBirthDate] = useState('');

  const [errors, setErrors] = useState<Errors>({});
  const [fee, setFee] = useState<MvrOrderFee | null>(null);
  const [feeLoading, setFeeLoading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [ordering, setOrdering] = useState(false);
  const [order, setOrder] = useState<MvrOrderResult | null>(null);
  const [statusText, setStatusText] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [rateLimited, setRateLimited] = useState(false);

  const pollRef = useRef<number | null>(null);
  const attemptsRef = useRef(0);

  const driver = useMemo(
    () => (selectedIdx >= 0 ? drivers[selectedIdx] : undefined),
    [drivers, selectedIdx],
  );

  // Prefill from the driver; every field stays editable.
  useEffect(() => {
    if (!driver) return;
    const n = splitName(driver);
    setFirstName(n.firstName);
    setMiddleName(n.middleName);
    setLastName(n.lastName);
    setLicenseState(stateCode(driver.state || driver.licenseState || ''));
    setLicenseNumber(driver.licenseNumber || '');
    setBirthDate(toIsoDate(driver.dateOfBirth));
    setErrors({});
  }, [driver]);

  // One call both prices the order and returns the record types the state offers,
  // so the dropdown is never a stale copy of the fee table.
  useEffect(() => {
    // Activity History is a flat fee, so quote it without waiting for a state -
    // otherwise the From Date it returns stays blank until one is picked.
    if (!licenseState && flow !== MVR_FLOW_ACTIVITY_HISTORY) { setFee(null); return; }
    let cancelled = false;
    setFeeLoading(true);
    getMvrOrderFee(flow, licenseState, recordType || undefined)
      .then((f) => {
        if (cancelled) return;
        setFee(f);
        // Drop a record type the newly picked state does not offer.
        if (recordType && !f.recordTypes.includes(recordType)) setRecordType('');
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Could not price this order.');
      })
      .finally(() => { if (!cancelled) setFeeLoading(false); });
    return () => { cancelled = true; };
  }, [flow, licenseState, recordType]);

  const stopPolling = useCallback(() => {
    if (pollRef.current !== null) {
      window.clearTimeout(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  useEffect(() => stopPolling, [stopPolling]);

  /** Same rules and wording as the Toolbox. */
  const validate = (): boolean => {
    const next: Errors = {};
    if (!firstName.trim()) next.firstName = 'Please enter first name.';
    if (!lastName.trim()) next.lastName = 'Please enter last name.';
    if (!licenseState) next.licenseState = 'Please select license state.';
    if (flow === MVR_FLOW_TRANSACTIONAL && !recordType) next.recordType = 'Please select year.';
    if (!licenseNumber.trim()) {
      next.licenseNumber = 'Please enter license number.';
    } else if (licenseState && !isValidLicense(licenseNumber.trim(), { states: licenseState })) {
      // Catch a malformed number here, not after DataFabric has been charged.
      next.licenseNumber = 'Please enter valid license number.';
    }

    if (!birthDate) {
      next.birthDate = 'Please enter birth date.';
    } else {
      const age = ageInYears(birthDate);
      if (Number.isNaN(age)) next.birthDate = 'Please enter birth date.';
      else if (age < MIN_DRIVER_AGE_YEARS) {
        next.birthDate = `Birth date must be at least ${MIN_DRIVER_AGE_YEARS} years earlier than today.`;
      }
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const poll = useCallback((orderId: string) => {
    const tick = async () => {
      attemptsRef.current += 1;
      try {
        const s = await getMvrOrderStatus(quoteId, flow, orderId, quoteId);
        setStatusText(s.orderStatus || 'Pending');
        if (s.isComplete && s.reportId) {
          stopPolling();
          onOrderComplete(orderId, s.reportId, flow);
          return;
        }
      } catch (err) {
        // A failed poll is not a failed order.
        setStatusText(err instanceof Error ? err.message : 'Could not read order status.');
      }
      if (attemptsRef.current >= POLL_MAX_ATTEMPTS) {
        stopPolling();
        setStatusText('Still processing. Once the vendor completes it, add it with "Import existing MVR".');
        return;
      }
      pollRef.current = window.setTimeout(tick, POLL_INTERVAL_MS);
    };
    attemptsRef.current = 0;
    pollRef.current = window.setTimeout(tick, POLL_INTERVAL_MS);
  }, [flow, quoteId, onOrderComplete, stopPolling]);

  const onReview = () => {
    setError(null);
    if (validate()) setConfirming(true);
  };

  const submit = async () => {
    setOrdering(true);
    setError(null);
    try {
      const placed = await placeMvrOrder({
        quoteId,
        flow,
        firstName: firstName.trim(),
        middleName: middleName.trim() || undefined,
        lastName: lastName.trim(),
        licenseNumber: licenseNumber.trim(),
        licenseState,
        birthDate: birthDate || undefined,
        gender: driver?.gender,
        recordType: flow === MVR_FLOW_TRANSACTIONAL ? recordType : undefined,
        searchStartDate: fee?.searchStartDate,
        clientName: insuredName,
      });
      setOrder(placed);
      setConfirming(false);

      if (!placed.ordered) {
        // DataFabric declines with a 200 and a message — usually credits.
        setError(placed.message || 'The order was not placed.');
        return;
      }
      setStatusText('Ordered');
      if (placed.orderId) poll(placed.orderId);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to place the MVR order.';
      if (/429|rate limit/i.test(msg)) setRateLimited(true);
      else setError(msg);
      setConfirming(false);
    } finally {
      setOrdering(false);
    }
  };

  const placed = !!order?.ordered;

  return (
    <div className="omvr-overlay" onClick={onClose}>
      <div className="omvr-panel" onClick={(e) => e.stopPropagation()}>
        <div className="omvr-header">
          <span className="omvr-title">Order MVR</span>
          <button className="omvr-close" onClick={onClose} aria-label="Close">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <div className="omvr-body">
          {drivers.length === 0 ? (
            <div className="omvr-empty">No drivers on this quote to order an MVR for.</div>
          ) : rateLimited ? (
            <div className="omvr-note omvr-note--info">{RATE_LIMIT_MESSAGE}</div>
          ) : placed ? (
            <div className="omvr-note omvr-note--ok">
              <div className="omvr-note__title">Order placed</div>
              <div className="omvr-kv"><span>Order ID</span><span className="omvr-mono">{order?.orderId}</span></div>
              <div className="omvr-kv"><span>Charged</span><span>${order?.apiCost.toFixed(2)}</span></div>
              {statusText && <div className="omvr-kv"><span>Status</span><span>{statusText}</span></div>}
            </div>
          ) : (
            <>
              <div className="omvr-field">
                <label className="omvr-label">Driver</label>
                <select
                  className="omvr-input"
                  value={selectedIdx}
                  onChange={(e) => setSelectedIdx(Number(e.target.value))}
                >
                  {drivers.map((d, i) => <option key={i} value={i}>{driverLabel(d)}</option>)}
                </select>
              </div>

              <div className="omvr-field">
                <label className="omvr-label">Record type</label>
                <div className="omvr-radios">
                  <label className={`omvr-radio${flow === MVR_FLOW_TRANSACTIONAL ? " omvr-radio--on" : ""}`}>
                    <input
                      type="radio"
                      checked={flow === MVR_FLOW_TRANSACTIONAL}
                      onChange={() => setFlow(MVR_FLOW_TRANSACTIONAL)}
                    />
                    <span>
                      Transactional MVR
                      <span className="omvr-radio__hint">Priced per state and year</span>
                    </span>
                  </label>
                  <label className={`omvr-radio${flow === MVR_FLOW_ACTIVITY_HISTORY ? " omvr-radio--on" : ""}`}>
                    <input
                      type="radio"
                      checked={flow === MVR_FLOW_ACTIVITY_HISTORY}
                      onChange={() => setFlow(MVR_FLOW_ACTIVITY_HISTORY)}
                    />
                    <span>
                      Activity History
                      <span className="omvr-radio__hint">Flat fee, 3-year lookback</span>
                    </span>
                  </label>
                </div>
              </div>

              {flow === MVR_FLOW_ACTIVITY_HISTORY && (
                <div className="omvr-field">
                  <label className="omvr-label">From date</label>
                  <input className="omvr-input" type="date" value={fee?.searchStartDate ?? ''} readOnly disabled />
                  <div className="omvr-hint">Records are searched from this date forward.</div>
                </div>
              )}

              {flow === MVR_FLOW_TRANSACTIONAL && (
                <div className="omvr-field">
                  <label className="omvr-label">Year</label>
                  <select
                    className={`omvr-input${errors.recordType ? " omvr-input--err" : ""}`}
                    value={recordType}
                    onChange={(e) => setRecordType(e.target.value)}
                    disabled={!licenseState}
                  >
                    <option value="">
                      {licenseState ? 'Select year' : 'Select a license state first'}
                    </option>
                    {(fee?.recordTypes ?? []).map((rt) => <option key={rt} value={rt}>{rt}</option>)}
                  </select>
                  {errors.recordType && <div className="omvr-err">{errors.recordType}</div>}
                </div>
              )}

              <div className="omvr-row">
                <div className="omvr-field">
                  <label className="omvr-label">First name</label>
                  <input
                    className={`omvr-input${errors.firstName ? " omvr-input--err" : ""}`}
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                  />
                  {errors.firstName && <div className="omvr-err">{errors.firstName}</div>}
                </div>
                <div className="omvr-field">
                  <label className="omvr-label">Middle</label>
                  <input className="omvr-input" value={middleName} onChange={(e) => setMiddleName(e.target.value)} />
                </div>
                <div className="omvr-field">
                  <label className="omvr-label">Last name</label>
                  <input
                    className={`omvr-input${errors.lastName ? " omvr-input--err" : ""}`}
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                  />
                  {errors.lastName && <div className="omvr-err">{errors.lastName}</div>}
                </div>
              </div>

              <div className="omvr-row">
                <div className="omvr-field">
                  <label className="omvr-label">License state</label>
                  <select
                    className={`omvr-input${errors.licenseState ? " omvr-input--err" : ""}`}
                    value={licenseState}
                    onChange={(e) => setLicenseState(e.target.value)}
                  >
                    <option value="">—</option>
                    {US_STATES.map((st) => <option key={st.code} value={st.code}>{st.name}</option>)}
                  </select>
                  {errors.licenseState && <div className="omvr-err">{errors.licenseState}</div>}
                </div>
                <div className="omvr-field">
                  <label className="omvr-label">License number</label>
                  <input
                    className={`omvr-input${errors.licenseNumber ? " omvr-input--err" : ""}`}
                    value={licenseNumber}
                    onChange={(e) => setLicenseNumber(e.target.value)}
                  />
                  {errors.licenseNumber && <div className="omvr-err">{errors.licenseNumber}</div>}
                </div>
                <div className="omvr-field">
                  <label className="omvr-label">Date of birth</label>
                  <input
                    type="date"
                    className={`omvr-input${errors.birthDate ? " omvr-input--err" : ""}`}
                    value={birthDate}
                    onChange={(e) => setBirthDate(e.target.value)}
                  />
                  {errors.birthDate && <div className="omvr-err">{errors.birthDate}</div>}
                </div>
              </div>

              {confirming && (
                <div className="omvr-confirm">
                  <div className="omvr-confirm__lead">
                    Your account will be charged a total of{' '}
                    <strong>${fee ? fee.totalFee.toFixed(2) : '—'}</strong> for this MVR record.
                    Do you wish to proceed?
                  </div>
                  <div className="omvr-kv"><span>Service fee</span><span>${fee ? fee.serviceFee.toFixed(2) : '0.00'}</span></div>
                  <div className="omvr-kv"><span>State fee</span><span>${fee ? fee.stateFee.toFixed(2) : '0.00'}</span></div>
                  <div className="omvr-kv omvr-kv--total"><span>Total</span><span>${fee ? fee.totalFee.toFixed(2) : '—'}</span></div>
                </div>
              )}

              {!confirming && (
                <div className="omvr-cost">
                  {feeLoading ? <><span className="mvrp-spinner mvrp-spinner--dark" /> Pricing…</>
                    : fee && fee.totalFee > 0
                      ? <><span>Total charge</span><strong>${fee.totalFee.toFixed(2)}</strong></>
                      : flow === MVR_FLOW_TRANSACTIONAL && licenseState
                        ? <span>Select a year to see the cost.</span>
                        : <span>Select a license state to see the cost.</span>}
                </div>
              )}

              {error && <div className="omvr-note omvr-note--err">{error}</div>}
            </>
          )}
        </div>

        {drivers.length > 0 && !placed && !rateLimited && (
          <div className="omvr-footer">
            {confirming ? (
              <>
                <button className="omvr-btn" onClick={() => setConfirming(false)} disabled={ordering}>
                  Cancel
                </button>
                <button className="omvr-btn omvr-btn--primary" onClick={submit} disabled={ordering}>
                  {ordering ? <><span className="mvrp-spinner" /> Ordering…</> : 'Confirm & order'}
                </button>
              </>
            ) : (
              <button className="omvr-btn omvr-btn--primary" onClick={onReview} disabled={ordering}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="9" /><path d="M12 8v8" /><path d="M8 12h8" />
                </svg>
                Order MVR
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default OrderMvrModal;
